import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OrganizationSelector from './OrganizationSelector';
import DeviceMappingMenuContent from './DeviceMappingMenuContent';
import { useGlobalContext } from '../contexts/GlobalContext';

const drawerWidth = 280;
const drawerWidthCollapsed = 64;

const openedMixin = theme => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = theme => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: drawerWidthCollapsed,
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    [`& .${drawerClasses.paper}`]: openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    [`& .${drawerClasses.paper}`]: closedMixin(theme),
  }),
}));

export default function DeviceMappingSideMenu({ user, onLogout }) {
  const navigate = useNavigate();
  const { sidebarOpen, setSidebarOpen } = useGlobalContext();

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Drawer
      variant="permanent"
      open={sidebarOpen}
      sx={{
        display: { xs: 'none', md: 'block' },
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: 'background.paper',
        },
      }}
    >
      {/* Toggle button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          p: 1,
          minHeight: 48,
        }}
      >
        <Tooltip title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
          <IconButton onClick={handleToggleSidebar} size="small">
            {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          mt: 'calc(var(--template-frame-height, 0px) + 4px)',
          p: 1.5,
          opacity: sidebarOpen ? 1 : 0,
          transition: theme =>
            theme.transitions.create('opacity', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        {sidebarOpen && <OrganizationSelector />}
      </Box>
      <Divider />
      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DeviceMappingMenuContent collapsed={!sidebarOpen} />
      </Box>
      {/* Logo section below the divider */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: sidebarOpen ? 'flex-start' : 'center',
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 1.5,
          opacity: sidebarOpen ? 1 : 0,
          transition: theme =>
            theme.transitions.create('opacity', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        {sidebarOpen && (
          <>
            <img
              src="/app-logo.png"
              alt="FloorPlan Mapper Logo"
              style={{
                maxWidth: '120px',
                maxHeight: '60px',
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontSize: '1rem',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              FloorPlan Mapper
            </Typography>
          </>
        )}
      </Box>
      <Stack
        direction="row"
        sx={{
          p: 2,
          gap: 1,
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: sidebarOpen ? 'flex-start' : 'center',
        }}
      >
        {sidebarOpen ? (
          <>
            <ButtonBase
              onClick={handleProfileClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: 1,
                p: 0.5,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Avatar
                sizes="small"
                alt={user?.first_name || user?.username || 'User'}
                src="/static/images/avatar/7.jpg"
                sx={{ width: 36, height: 36 }}
              />
              <Box sx={{ textAlign: 'left' }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, lineHeight: '16px' }}
                >
                  {user?.first_name || user?.username || 'User'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {user?.email || 'user@email.com'}
                </Typography>
              </Box>
            </ButtonBase>
            <Box sx={{ ml: 'auto' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={onLogout}
                sx={{ fontSize: '0.75rem', px: 1 }}
              >
                Logout
              </Button>
            </Box>
          </>
        ) : (
          <Tooltip title="Profile">
            <IconButton onClick={handleProfileClick} size="small">
              <Avatar
                sizes="small"
                alt={user?.first_name || user?.username || 'User'}
                src="/static/images/avatar/7.jpg"
                sx={{ width: 32, height: 32 }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Drawer>
  );
}
